import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabase';

export const LIMITES = {
  livros: 20,
  clubes: 2,
  sugestoes_mes: 5,
  explorar_dia: 10,
  emprestimos_ativos: 3,
};

const MOTIVOS = {
  livros: {
    titulo: 'Limite de livros atingido',
    desc: `O plano grátis permite até ${LIMITES.livros} livros no acervo.`,
  },
  clubes: {
    titulo: 'Limite de clubes atingido',
    desc: `O plano grátis permite participar de até ${LIMITES.clubes} clubes.`,
  },
  explorar: {
    titulo: 'Swipes de hoje esgotados',
    desc: `O plano grátis permite ${LIMITES.explorar_dia} swipes por dia no Explorar.`,
  },
  sugestoes: {
    titulo: 'Sugestões do mês esgotadas',
    desc: `O plano grátis exibe até ${LIMITES.sugestoes_mes} sugestões de leitura por mês.`,
  },
  emprestimos: {
    titulo: 'Limite de empréstimos atingido',
    desc: `O plano grátis permite até ${LIMITES.emprestimos_ativos} empréstimos ativos ao mesmo tempo.`,
  },
};


const PlanContext = createContext(null);

export function usePlan() {
  return useContext(PlanContext);
}

function UpgradeModal({ motivo, onClose, userId }) {
  const info = MOTIVOS[motivo] || { titulo: 'Seja Premium', desc: 'Desbloqueie tudo com um pagamento único.' };
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');

  async function handleCheckout() {
    if (!userId) { setErro('Usuário não identificado. Tente novamente.'); return; }
    setCarregando(true);
    setErro('');
    try {
      const res = await fetch('https://bibliotecadebolso.app/.netlify/functions/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch {
        setErro(`Erro interno (${res.status}). Tente novamente.`);
        setCarregando(false);
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      } else {
        setErro(data.error || 'Erro ao iniciar pagamento. Tente novamente.');
        setCarregando(false);
      }
    } catch (err) {
      console.error('Erro fetch checkout:', err);
      setErro('Erro de conexão. Tente novamente.');
      setCarregando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      style={{ background: 'rgba(33,28,43,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-sm mx-4 mb-6 sm:mb-0 rounded-[28px] overflow-hidden"
        style={{ background: '#FAF8F5', boxShadow: '0 24px 64px rgba(33,28,43,0.28)' }}>

        {/* header roxo */}
        <div className="px-6 pt-8 pb-6 text-center"
          style={{ background: 'linear-gradient(145deg, #5B3CE4, #3F27B0)' }}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ background: 'rgba(255,255,255,0.18)' }}>
            <span className="text-[26px]">✨</span>
          </div>
          <span className="inline-block px-3 py-0.5 rounded-full text-[11px] font-extrabold tracking-widest uppercase mb-2"
            style={{ background: '#FFAA00', color: '#1A1108' }}>Premium</span>
          <h2 className="text-[20px] font-extrabold text-white m-0">Biblioteca de Bolso</h2>
          <p className="text-[13px] font-medium mt-1 m-0" style={{ color: 'rgba(255,255,255,0.72)' }}>
            Pagamento único · Sem assinaturas
          </p>
        </div>

        <div className="px-6 pt-5 pb-3">
          {/* motivo */}
          <div className="rounded-[14px] px-4 py-3 mb-4 text-center"
            style={{ background: '#F0ECFE', border: '1px solid #CFC5F7' }}>
            <p className="text-[13px] font-semibold m-0" style={{ color: '#3F27B0' }}>{info.titulo}</p>
            <p className="text-[12px] text-ink-500 font-medium m-0 mt-0.5">{info.desc}</p>
          </div>

          {/* features */}
          <ul className="m-0 p-0 list-none space-y-2.5 mb-5">
            {[
              'Livros ilimitados no acervo',
              'Participar de clubes ilimitados',
              'Sugestões de leitura ilimitadas',
              'Explorar livros sem limite diário',
              'Empréstimos simultâneos ilimitados',
            ].map(f => (
              <li key={f} className="flex items-center gap-2.5">
                <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold"
                  style={{ background: '#E8F8F0', color: '#1FA971' }}>✓</span>
                <span className="text-[13.5px] font-medium text-ink-700">{f}</span>
              </li>
            ))}
          </ul>

          {/* CTA */}
          <button
            onClick={handleCheckout}
            disabled={carregando}
            className="block w-full py-[15px] rounded-[14px] text-[15px] font-extrabold text-center border-0 cursor-pointer disabled:opacity-60"
            style={{ background: '#5B3CE4', color: '#fff', boxShadow: '0 8px 20px rgba(91,60,228,0.30)' }}>
            {carregando ? 'Redirecionando…' : 'Desbloquear por R$ 29,90'}
          </button>
          {erro && <p className="text-[12px] text-red-500 text-center font-medium mt-2">{erro}</p>}
          <p className="text-[11px] text-ink-300 text-center font-medium mt-2 mb-1">
            Pagamento único · Acesso vitalício
          </p>
        </div>

        <button onClick={onClose}
          className="w-full py-3 text-[13px] font-semibold text-ink-400 border-0 bg-transparent cursor-pointer border-t"
          style={{ borderColor: '#EBE8F0' }}>
          Agora não
        </button>
      </div>
    </div>
  );
}

export function PlanProvider({ children }) {
  const [plano, setPlano] = useState('free');
  const [userId, setUserId] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeMotivo, setUpgradeMotivo] = useState(null);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const pollingRef = useRef(null);

  async function carregarPlano(uid) {
    const { data } = await supabase.from('perfis').select('plano').eq('id', uid).maybeSingle();
    if (data?.plano) { setPlano(data.plano); return data.plano; }
    return null;
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) { setUserId(session.user.id); carregarPlano(session.user.id); }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) { setUserId(session.user.id); carregarPlano(session.user.id); }
      else { setUserId(null); setPlano('free'); }
    });

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Detecta retorno do Stripe após pagamento e faz polling até o webhook atualizar o plano
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') !== 'success') return;
    window.history.replaceState({}, '', window.location.pathname);

    const uid = userId;
    if (!uid) return;

    let tentativas = 0;
    pollingRef.current = setInterval(async () => {
      tentativas++;
      const planoAtual = await carregarPlano(uid);
      if (planoAtual === 'pago' || tentativas >= 10) {
        clearInterval(pollingRef.current);
        if (planoAtual === 'pago') setUpgradeSuccess(true);
      }
    }, 1500);

    return () => clearInterval(pollingRef.current);
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPago = plano === 'pago';

  const openUpgrade = useCallback((motivo) => {
    setUpgradeMotivo(motivo || null);
    setUpgradeOpen(true);
  }, []);

  const closeUpgrade = useCallback(() => {
    setUpgradeOpen(false);
    setUpgradeMotivo(null);
  }, []);

  return (
    <PlanContext.Provider value={{ plano, isPago, openUpgrade, userId, LIMITES }}>
      {children}
      {upgradeOpen && <UpgradeModal motivo={upgradeMotivo} onClose={closeUpgrade} userId={userId} />}
      {upgradeSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{background:'rgba(33,28,43,0.55)', backdropFilter:'blur(6px)'}}>
          <div className="bg-white rounded-[28px] px-8 py-10 text-center max-w-xs mx-4" style={{boxShadow:'0 24px 64px rgba(33,28,43,0.28)'}}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{background:'linear-gradient(145deg,#5B3CE4,#3F27B0)'}}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <span className="inline-block px-3 py-0.5 rounded-full text-[11px] font-extrabold tracking-widest uppercase mb-3"
              style={{background:'#FFAA00', color:'#1A1108'}}>Premium</span>
            <h2 className="text-[22px] font-extrabold text-ink-900 m-0 mb-2">Bem-vindo ao Premium!</h2>
            <p className="text-[14px] text-ink-500 font-medium mb-6">Seu acesso completo esta ativado. Aproveite tudo que o Biblioteca de Bolso tem a oferecer.</p>
            <button onClick={() => setUpgradeSuccess(false)}
              className="w-full py-3 rounded-[14px] text-[15px] font-extrabold text-white border-0 cursor-pointer"
              style={{background:'#5B3CE4', boxShadow:'0 8px 20px rgba(91,60,228,0.30)'}}>
              Explorar agora
            </button>
          </div>
        </div>
      )}
    </PlanContext.Provider>
  );
}
